import { defineStore } from "pinia";
import { ref } from "vue";

import { ApiError } from "@/types/api";

export type RuntimeFailureCategory =
  | "render"
  | "navigation"
  | "unhandled-rejection";

export type RuntimeRecoveryAction = "retry-view" | "go-home" | "reload";

export interface RuntimeFailure {
  actions: RuntimeRecoveryAction[];
  category: RuntimeFailureCategory;
  message: string;
  occurredAt: string;
  status?: number;
  title: string;
}

const SAFE_RUNTIME_COPY: Record<
  RuntimeFailureCategory,
  Pick<RuntimeFailure, "message" | "title">
> = {
  render: {
    title: "This view could not continue",
    message:
      "Your existing work is still safe. Retry this view or return home.",
  },
  navigation: {
    title: "This page could not be opened",
    message: "Return home or reload ApplyKitty, then try again.",
  },
  "unhandled-rejection": {
    title: "An unexpected operation failed",
    message:
      "Your existing work is still safe. Return home or reload ApplyKitty.",
  },
};

export const useRuntimeErrorStore = defineStore("runtimeError", () => {
  const failure = ref<RuntimeFailure | null>(null);

  function capture(
    category: RuntimeFailureCategory,
    caught: unknown,
    actions: RuntimeRecoveryAction[],
  ): void {
    if (failure.value !== null) {
      return;
    }

    const status =
      caught instanceof ApiError && typeof caught.status === "number"
        ? caught.status
        : undefined;
    const copy = SAFE_RUNTIME_COPY[category];

    failure.value = {
      actions: [...new Set(actions)],
      category,
      message: copy.message,
      occurredAt: new Date().toISOString(),
      ...(status === undefined ? {} : { status }),
      title: copy.title,
    };
  }

  function clear(): void {
    failure.value = null;
  }

  return { capture, clear, failure };
});
